(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inEditor = Boolean(window.Shopify?.designMode);
  const strings = () => JSON.parse(document.getElementById('PrismStrings')?.textContent || '{}');
  const listen = (element, event, handler, signal, extra = {}) => element?.addEventListener(event, handler, { signal, ...extra });

  class PrismElement extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.setup(this.controller.signal);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
    }
  }

  class Announcement extends PrismElement {
    setup(signal) {
      this.messages = [...this.querySelectorAll('[data-announcement-message]')];
      this.index = 0;
      this.paused = reduceMotion.matches;
      this.pointerInside = false;
      this.focusInside = false;
      if (this.messages.length < 2) {
        this.querySelectorAll('button').forEach(button => button.hidden = true);
        return;
      }
      const show = index => {
        this.index = (index + this.messages.length) % this.messages.length;
        this.messages.forEach((message, i) => {
          message.hidden = i !== this.index;
          message.classList.toggle('is-active', i === this.index);
        });
      };
      listen(this.querySelector('[data-announcement-prev]'), 'click', () => show(this.index - 1), signal);
      listen(this.querySelector('[data-announcement-next]'), 'click', () => show(this.index + 1), signal);
      listen(this, 'pointerenter', () => { this.pointerInside = true; }, signal);
      listen(this, 'pointerleave', () => { this.pointerInside = false; }, signal);
      listen(this, 'focusin', () => { this.focusInside = true; }, signal);
      listen(this, 'focusout', event => { this.focusInside = this.contains(event.relatedTarget); }, signal);
      listen(this, 'shopify:block:select', event => {
        const selected = event.target.closest('[data-announcement-message]');
        if (selected) { show(this.messages.indexOf(selected)); this.paused = true; }
      }, signal);
      listen(this, 'shopify:block:deselect', () => { this.paused = reduceMotion.matches; }, signal);
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden && !this.focusInside) show(this.index + 1);
      }, Number(this.dataset.interval) || 5000);
      show(0);
    }
  }

  class Header extends PrismElement {
    setup() {
      this.closest('.shopify-section')?.classList.toggle('header-section-sticky', this.dataset.sticky === 'true');
    }
  }

  class Counters extends PrismElement {
    setup() {
      const counters = [...this.querySelectorAll('[data-counter]')];
      const numbers = counters.map(node => ({ node, value: Number(String(node.dataset.counter).replace(/,/g, '')), decimals: Number(node.dataset.decimals) || 0 }));
      const format = (value, decimals) => value.toLocaleString(document.documentElement.lang || 'en', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      const reset = () => numbers.forEach(({ node, decimals }) => { node.textContent = format(0, decimals); });
      const finish = () => numbers.forEach(({ node, value, decimals }) => { node.textContent = format(value, decimals); });
      const animate = () => {
        if (reduceMotion.matches || inEditor) { finish(); return; }
        const start = performance.now();
        const duration = Number(this.dataset.duration) || 2000;
        const tick = now => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          numbers.forEach(({ node, value, decimals }) => { node.textContent = format(value * eased, decimals); });
          if (progress < 1) this.frame = requestAnimationFrame(tick);
          else finish();
        };
        this.frame = requestAnimationFrame(tick);
      };
      this.restart = () => {
        cancelAnimationFrame(this.frame);
        this.observer?.disconnect();
        if (reduceMotion.matches || inEditor || !('IntersectionObserver' in window)) { finish(); return; }
        reset();
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); animate(); }
        }, { threshold: 0.15 });
        this.observer.observe(this);
      };
      this.restart();
    }
  }

  class Marquee extends PrismElement {
    setup(signal) {
      this.querySelectorAll('.marquee-group[aria-hidden]').forEach(node => node.remove());
      this.querySelectorAll('.marquee-group').forEach(group => {
        if (!group.children.length) { group.closest('.marquee-window').hidden = true; return; }
        const clone = group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[data-shopify-editor-block], [id]').forEach(node => { node.removeAttribute('data-shopify-editor-block'); node.removeAttribute('id'); });
        group.parentElement.append(clone);
      });
      const section = this.closest('.reviews');
      listen(this, 'shopify:block:select', () => { section.classList.add('is-paused'); }, signal);
      listen(this, 'shopify:block:deselect', () => { section.classList.remove('is-paused'); }, signal);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => entries.forEach(entry => this.classList.toggle('is-visible', entry.isIntersecting)), { rootMargin: '100px' });
        this.observer.observe(this);
      } else this.classList.add('is-visible');
    }
  }

  class Gallery extends PrismElement {
    setup(signal) {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('.gallery-slide')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.index = 0;
      const paint = () => {
        const width = this.track.clientWidth;
        if (!width) return;
        const current = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
        if (this.index !== current) this.slides[this.index]?.querySelectorAll('video').forEach(video => video.pause());
        this.index = current;
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === current)));
        this.querySelectorAll('[data-gallery-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === current));
      };
      listen(this.track, 'scroll', () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(paint); }, signal, { passive: true });
      listen(this.querySelector('[data-gallery-prev]'), 'click', () => this.go(this.index - 1), signal);
      listen(this.querySelector('[data-gallery-next]'), 'click', () => this.go(this.index + 1), signal);
      this.thumbs.forEach((thumb, i) => listen(thumb, 'click', () => this.go(i), signal));
      listen(this.track, 'keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1)); }
      }, signal);
      this.resizeObserver = new ResizeObserver(() => this.go(this.index, true));
      this.resizeObserver.observe(this.track);
      paint();
    }
    go(index, instant = false) {
      index = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: instant || reduceMotion.matches ? 'instant' : 'smooth' });
    }
    showMedia(id) {
      const index = this.slides.findIndex(slide => slide.dataset.mediaId === String(id));
      if (index >= 0) this.go(index);
    }
  }

  class Quantity extends PrismElement {
    setup(signal) {
      const input = this.querySelector('input');
      const change = direction => {
        const min = Number(input.min) || 0;
        const max = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step) || 1;
        const current = Number(input.value) || min;
        const cartMin = Number(input.dataset.quantityMin) || min;
        const next = current + step * direction;
        input.value = input.hasAttribute('data-cart-quantity') && next < cartMin ? (direction > 0 ? cartMin : 0) : Math.max(min, Math.min(max, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      listen(this.querySelector('[data-quantity-minus]'), 'click', () => change(-1), signal);
      listen(this.querySelector('[data-quantity-plus]'), 'click', () => change(1), signal);
    }
  }

  class Product extends PrismElement {
    setup(signal) {
      this.form = this.querySelector('form');
      this.button = this.form?.querySelector('[data-submit]');
      this.addText = this.dataset.addLabel || strings().add;
      this.sticky = this.closest('.product-section').querySelector('[data-sticky-add]');
      const stickyButton = this.sticky?.querySelector('button');
      const selector = this.querySelector('[data-variant]');
      listen(selector, 'change', () => {
        const option = selector.selectedOptions[0];
        const available = option.dataset.available === 'true';
        this.querySelector('[data-product-price]').textContent = option.dataset.price;
        const compare = this.querySelector('[data-product-compare]');
        compare.hidden = option.dataset.onSale !== 'true';
        compare.textContent = option.dataset.compare;
        this.button.disabled = !available;
        this.button.textContent = available ? this.addText : strings().soldOut;
        if (stickyButton) { stickyButton.disabled = !available; stickyButton.textContent = this.button.textContent; }
        const quantity = this.form.querySelector('input[name=quantity]');
        if (quantity) { quantity.min = option.dataset.min || '1'; quantity.step = option.dataset.step || '1'; quantity.max = option.dataset.max || ''; quantity.value = quantity.min; }
        const url = new URL(location.href);
        url.searchParams.set('variant', option.value);
        history.replaceState({}, '', url);
        if (option.dataset.mediaId) this.closest('.product-layout').querySelector('prism-gallery')?.showMedia(option.dataset.mediaId);
        this.form.querySelector('[data-form-error]').hidden = true;
      }, signal);
      if (this.sticky && this.button) {
        listen(stickyButton, 'click', () => this.form.requestSubmit(this.button), signal);
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
          document.body.style.paddingBottom = this.sticky.hidden ? '' : `${this.sticky.offsetHeight}px`;
        }, { threshold: 0 });
        this.observer.observe(this.button);
      }
    }
    disconnectedCallback() { super.disconnectedCallback(); document.body.style.paddingBottom = ''; }
  }


  function instagramURL(value) {
    let raw = String(value || '').trim().replace(/&amp;/g, '&');
    if (!raw) return null;
    if (raw.startsWith('//')) raw = 'https:' + raw;
    if (!/^https?:\/\//i.test(raw)) raw = 'https://' + raw;
    try {
      const url = new URL(raw);
      if (!['instagram.com', 'www.instagram.com', 'm.instagram.com'].includes(url.hostname.toLowerCase()) || url.username || url.password) return null;
      const match = url.pathname.match(/^\/(?:[A-Za-z0-9_.]+\/)?(p|reel|reels|tv)\/([A-Za-z0-9_-]+)\/?$/);
      return match ? `https://www.instagram.com/${match[1] === 'reels' ? 'reel' : match[1]}/${match[2]}/` : null;
    } catch { return null; }
  }

  let instagramLoader;
  let instagramProcessQueued = false;
  function processInstagram() {
    if (instagramProcessQueued) return;
    instagramProcessQueued = true;
    requestAnimationFrame(() => {
      instagramProcessQueued = false;
      window.instgrm?.Embeds?.process();
    });
  }

  function loadInstagram() {
    if (window.instgrm?.Embeds) return Promise.resolve();
    if (instagramLoader) return instagramLoader;
    instagramLoader = new Promise((resolve, reject) => {
      let script = document.querySelector('script[data-instagram-embed]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.dataset.instagramEmbed = '';
      }
      const finish = success => {
        clearTimeout(timer);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
        if (success) resolve();
        else { script.remove(); reject(new Error('Instagram unavailable')); }
      };
      const loaded = () => finish(Boolean(window.instgrm?.Embeds));
      const failed = () => finish(false);
      const timer = setTimeout(failed, 15000);
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      if (!script.isConnected) document.head.append(script);
    }).catch(error => {
      instagramLoader = null;
      throw error;
    });
    return instagramLoader;
  }

  class InstagramPost extends PrismElement {
    setup(signal) {
      this.embedObserver?.disconnect();
      this.querySelector('.instagram-embed')?.remove();
      this.started = false;
      this.classList.remove('is-embedded');
      const fallback = this.querySelector('.social-post-fallback');
      if (fallback) fallback.hidden = false;
      this.classList.toggle('has-preview', Boolean(fallback));
      const url = instagramURL(this.dataset.url);
      if (!url) return;
      this.classList.add('has-instagram-url');
      let link = this.querySelector('.instagram-post-link');
      if (!link) { link = document.createElement('a'); link.className = 'instagram-post-link'; link.textContent = this.dataset.label || 'Instagram'; this.append(link); }
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      const mount = async () => {
        if (this.started) return;
        this.started = true;
        this.observer?.disconnect();
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
        const container = document.createElement('div');
        container.className = 'instagram-embed';
        if (fallback) container.setAttribute('aria-hidden', 'true');
        const embed = document.createElement('blockquote');
        embed.className = 'instagram-media';
        embed.dataset.instgrmPermalink = url;
        embed.dataset.instgrmVersion = '14';
        const fallbackLink = document.createElement('a');
        fallbackLink.href = url; fallbackLink.textContent = this.dataset.label || 'Instagram';
        embed.append(fallbackLink);
        container.append(embed);
        this.prepend(container);
        const watched = new WeakSet();
        const watchFrames = () => {
          container.querySelectorAll('iframe').forEach(frame => {
            if (watched.has(frame)) return;
            watched.add(frame);
            frame.loading = 'eager';
            if (fallback) frame.tabIndex = -1;
            listen(frame, 'load', () => {
              if (!frame.getAttribute('src') || frame.getAttribute('src') === 'about:blank') return;
              this.classList.add('is-embedded');
              this.setAttribute('aria-busy', 'false');
              container.removeAttribute('aria-hidden');
              frame.removeAttribute('tabindex');
              if (fallback) fallback.hidden = true;
              this.embedObserver.disconnect();
            }, signal);
          });
        };
        this.embedObserver = new MutationObserver(watchFrames);
        this.embedObserver.observe(container, { childList: true, subtree: true });
        try { await loadInstagram(); if (!signal.aborted && this.isConnected) processInstagram(); }
        catch {
          container.remove();
          this.embedObserver?.disconnect();
          this.setAttribute('aria-busy', 'false');
          this.started = false;
        }
      };
      if (this.dataset.load === 'eager' || !('IntersectionObserver' in window) || inEditor) mount();
      else {
        this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) mount(); }, { rootMargin: '1200px' });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() { super.disconnectedCallback(); this.embedObserver?.disconnect(); }
  }

  const elements = { 'prism-announcement': Announcement, 'prism-header': Header, 'prism-counters': Counters, 'prism-marquee': Marquee, 'prism-gallery': Gallery, 'prism-quantity': Quantity, 'prism-product': Product, 'prism-instagram-post': InstagramPost };
  Object.entries(elements).forEach(([name, element]) => { if (!customElements.get(name)) customElements.define(name, element); });

  let revealObserver;
  function reveal(root = document) {
    const nodes = [...root.querySelectorAll('[data-reveal]')];
    if (root.matches?.('[data-reveal]')) nodes.push(root);
    if (reduceMotion.matches || inEditor || document.body.dataset.revealEnabled !== 'true' || !('IntersectionObserver' in window)) {
      nodes.forEach(node => node.classList.remove('reveal-pending'));
      return;
    }
    revealObserver ||= new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.remove('reveal-pending'); revealObserver.unobserve(entry.target); }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px 30px 0px' });
    nodes.forEach(node => {
      if (node.dataset.revealReady) return;
      node.dataset.revealReady = 'true';
      if (node.getBoundingClientRect().top > innerHeight * 0.9) { node.classList.add('reveal-pending'); revealObserver.observe(node); }
    });
  }
  reveal();
  document.addEventListener('shopify:section:load', event => reveal(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-reveal]').forEach(node => revealObserver?.unobserve(node)));
  window.addEventListener('pageshow', event => {
    if (event.persisted) { document.querySelectorAll('prism-counters').forEach(counter => counter.restart?.()); document.body.classList.remove('menu-open'); }
  });
  reduceMotion.addEventListener('change', () => { if (reduceMotion.matches) document.querySelectorAll('.reveal-pending').forEach(node => node.classList.remove('reveal-pending')); });


  const drawer = () => document.querySelector('[data-cart-drawer]');
  let cartQueue = Promise.resolve();
  let pendingOperations = 0;
  let quantityTimer;
  let pendingNote;
  const pendingQuantities = new Map();
  let returnFocus;

  function setCartBusy(busy) {
    document.querySelectorAll('[data-cart-content]').forEach(content => {
      content.setAttribute('aria-busy', String(busy));
      content.querySelectorAll('input,button,textarea').forEach(input => { input.disabled = busy; });
    });
    const status = drawer()?.querySelector('[data-cart-status]');
    if (status) status.textContent = busy ? strings().cartLoading : '';
  }
  function queueCart(operation) {
    pendingOperations++;
    setCartBusy(true);
    const task = cartQueue.then(operation);
    cartQueue = task.catch(() => {});
    return task.finally(() => { pendingOperations--; setCartBusy(pendingOperations > 0); });
  }
  function cartError(message) {
    document.querySelectorAll('[data-cart-error]').forEach(node => { node.textContent = message || ''; node.hidden = !message; });
  }
  function renderCart(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-content]');
    if (!fresh) return false;
    const focused = document.activeElement;
    const focusLine = focused?.closest('[data-cart-line]')?.dataset.cartLine;
    const focusAttribute = ['data-quantity-plus','data-quantity-minus','data-cart-quantity','data-cart-remove','data-cart-note'].find(attr => focused?.hasAttribute(attr));
    const focusScope = focused?.closest('[data-cart-content]')?.dataset.cartScope;
    document.querySelectorAll('[data-cart-content]').forEach(content => {
      const scope = content.dataset.cartScope;
      const clone = fresh.cloneNode(true);
      clone.querySelectorAll('[id]').forEach(node => { node.id = node.id.replace('-drawer', '-' + scope); });
      clone.querySelectorAll('label[for]').forEach(node => { node.htmlFor = node.htmlFor.replace('-drawer', '-' + scope); });
      content.innerHTML = clone.innerHTML;
      content.dataset.itemCount = fresh.dataset.itemCount;
      if (scope === focusScope && focusAttribute) {
        const line = [...content.querySelectorAll('[data-cart-line]')].find(node => node.dataset.cartLine === focusLine);
        const target = (line || content).querySelector(`[${focusAttribute}]`);
        if (target) { target.disabled = false; target.focus({ preventScroll: true }); }
        else drawer()?.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
      }
    });
    const count = Number(fresh.dataset.itemCount) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; node.hidden = count === 0; });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count } }));
    return true;
  }
  async function refreshCart() {
    const target = drawer();
    if (!target) return;
    const url = new URL(strings().cartUrl, location.origin);
    url.searchParams.set('section_id', target.dataset.sectionId);
    const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok || !renderCart(await response.text())) throw new Error(strings().cartError);
  }
  function openCart() {
    const target = drawer();
    if (!target) return false;
    if (!target.open) { returnFocus = document.activeElement; target.showModal(); document.body.classList.add('cart-open'); }
    return true;
  }
  function closeCart() { drawer()?.close(); }
  async function changeCart(body, noteOnly = false) {
    const sectionId = drawer()?.dataset.sectionId;
    const response = await fetch(noteOnly ? strings().cartUpdate : strings().cartChange, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionId, sections_url: strings().cartUrl })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : result.message || strings().cartError);
    if (!renderCart(result.sections?.[sectionId])) await refreshCart();
  }
  function scheduleCart() {
    clearTimeout(quantityTimer);
    document.querySelectorAll('[data-cart-checkout]').forEach(button => { button.disabled = true; });
    quantityTimer = setTimeout(flushCart, 300);
  }
  function flushCart() {
    clearTimeout(quantityTimer);
    const changes = [...pendingQuantities.values()].sort((a,b) => b.line - a.line);
    const note = pendingNote;
    pendingQuantities.clear(); pendingNote = undefined;
    if (!changes.length && note === undefined) return cartQueue;
    cartError('');
    return queueCart(async () => {
      try {
        for (const change of changes) await changeCart(change);
        if (note !== undefined) await changeCart({ note }, true);
      } catch (error) {
        try { await refreshCart(); } catch {}
        cartError(error.message || strings().cartError);
      }
    });
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      if (!openCart()) return;
      event.preventDefault();
      cartError('');
      queueCart(refreshCart).catch(error => cartError(error.message));
    }
    if (event.target.closest('[data-cart-close]')) closeCart();
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      if (pendingOperations) return;
      const line = remove.closest('[data-cart-line]');
      pendingQuantities.set(line.dataset.lineKey, { line: Number(line.dataset.cartLine), quantity: 0 });
      flushCart();
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-cart-quantity]')) {
      const input = event.target;
      const line = input.closest('[data-cart-line]');
      const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 0 || !input.checkValidity()) { input.reportValidity(); return; }
      pendingQuantities.set(line.dataset.lineKey, { line: Number(line.dataset.cartLine), quantity });
      scheduleCart();
    }
    if (event.target.matches('[data-cart-note]')) { pendingNote = event.target.value; scheduleCart(); }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-cart-note]')) { pendingNote = event.target.value; scheduleCart(); }
  });
  function setupDrawer() {
    const target = drawer();
    if (!target || target.dataset.initialized) return;
    target.dataset.initialized = 'true';
    target.addEventListener('click', event => {
      const bounds = target.getBoundingClientRect();
      if (event.target === target && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) closeCart();
    });
    target.addEventListener('close', () => {
      document.body.classList.remove('cart-open');
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    });
  }
  setupDrawer();
  document.addEventListener('shopify:section:load', setupDrawer);
  document.addEventListener('shopify:section:select', event => { if (event.target.querySelector('[data-cart-drawer]')) openCart(); });
  document.addEventListener('shopify:section:deselect', event => { if (event.target.querySelector('[data-cart-drawer]')) closeCart(); });
  document.addEventListener('shopify:section:unload', event => { if (event.target.querySelector('[data-cart-drawer]')) document.body.classList.remove('cart-open'); });

  const busyForms = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.matches('[data-auto-cart-form]')) {
      if (event.submitter?.name !== 'checkout') { event.preventDefault(); flushCart(); }
      else if (pendingOperations || pendingQuantities.size || pendingNote !== undefined) { event.preventDefault(); await flushCart(); form.querySelector('[data-cart-checkout]')?.focus(); }
      return;
    }
    if (!form.matches('[data-cart-form]')) return;
    event.preventDefault();
    if (busyForms.has(form)) return;
    const button = form.querySelector('[data-submit]');
    const error = form.querySelector('[data-form-error]');
    const originalText = button.textContent;
    const stickyButton = form.closest('.product-section')?.querySelector('[data-sticky-submit]');
    const translations = strings();
    busyForms.add(form);
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    if (stickyButton) stickyButton.disabled = true;
    error.hidden = true;
    const data = new FormData(form);
    const sectionId = drawer()?.dataset.sectionId;
    data.set('sections', sectionId || 'prism-cart-drawer');
    data.set('sections_url', translations.cartUrl);
    let added = false;
    try {
      await queueCart(async () => {
        const response = await fetch(translations.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : result.message || translations.error);
        added = true;
        cartError(''); openCart();
        try { if (!renderCart(result.sections?.[sectionId])) await refreshCart(); }
        catch (failure) { cartError(failure.message || translations.cartError); }
      });
      const status = drawer()?.querySelector('[data-cart-status]');
      if (status) status.textContent = translations.added;
    } catch (failure) {
      error.textContent = failure.message || translations.error; error.hidden = false;
    } finally {
      busyForms.delete(form);
      const option = form.querySelector('[data-variant]')?.selectedOptions[0];
      const unavailable = option?.dataset.available === 'false';
      button.disabled = unavailable; button.removeAttribute('aria-busy');
      button.textContent = unavailable ? translations.soldOut : originalText;
      if (stickyButton) stickyButton.disabled = unavailable;
      if (!added && error) error.scrollIntoView({ behavior: reduceMotion.matches ? 'instant' : 'smooth', block: 'nearest' });
    }
  });
  const profileLink = document.querySelector('[data-instagram-profile]');
  const footerInstagram = document.querySelector('[data-footer-instagram]');
  if (profileLink && footerInstagram && footerInstagram.getAttribute('href').startsWith('/pages/social')) {
    footerInstagram.href = profileLink.href; footerInstagram.target = '_blank'; footerInstagram.rel = 'noopener';
  }
  if (location.hash === '#instagram-feed') document.querySelector('.instagram')?.scrollIntoView();
})();
